import { Grid, Link, Stack, Typography } from "@mui/material";
import type { FC } from "react";
import { useState } from "react";
import CustomerLink from "../components/customerLink/CustomerLink";
import EntityCard from "../components/entityCard/EntityCard";
import type { UserRole } from "../auth/auth";
import { formatDate } from "../data/dataUtils";
import { AccessTime as TimeIcon, CalendarMonth as CalendarMonthIcon, Person as PersonIcon } from "@mui/icons-material";
import { Link as RouterLink } from "react-router-dom";
import type { TaskParentResponse, UpdateTaskRequest } from "./tasksApi";
import TaskActions from "./TaskActions";
import dayjs from "../dayjs";
import EditTaskDialog from "./EditTaskDialog";

export const OVERDUE_COLOR = "rgb(251, 157, 154)";

function pathForParent({ type, id }: TaskParentResponse): string {
  switch (type) {
    case "CALCULATION":
      return `/calculations/${id}`;
    case "ORDER":
      return `/orders/${id}`;
    case "CUSTOMER":
      return `/customers/${id}`;
  }
}

function nameForParent(parent: TaskParentResponse) {
  switch (parent.type) {
    case "CALCULATION":
      return "Kalkulacja";
    case "ORDER":
      return `Zamówienie ${parent.userFriendlyId}`;
    case "CUSTOMER":
      return "Klient";
  }
}

const ParentLink: FC<{ parent: TaskParentResponse }> = ({ parent }) => {
  return (
    <Link component={RouterLink} to={pathForParent(parent)} target="_blank">
      {nameForParent(parent)}
    </Link>
  );
};

export interface Task {
  readonly id: string;
  readonly description: string;
  readonly due: {
    readonly date: string; // ISO date (YYYY-MM-DD)
    readonly time?: string; // HH:mm (optional)
  };
  readonly assignedUser: { id: string; name: string };
  readonly completed?: {
    readonly at: string;
    readonly by: string; // Email
    readonly note?: string;
  };
  readonly parent: TaskParentResponse;
  readonly customer?: { readonly id: string; readonly name: string };
  readonly createdBy?: { readonly name: string };
}

interface Props {
  readonly value: Task;
  readonly role: UserRole;
  readonly canDelete: boolean;
  readonly onClick: () => void;
  readonly onDelete: () => Promise<void>;
  readonly onComplete: (note?: string) => Promise<void>;
  readonly onUpdate: (request: UpdateTaskRequest) => Promise<void>;
}

export const TaskCard: FC<Props> = ({ value, role, canDelete, onClick, onComplete, onDelete, onUpdate }) => {
  const isOverdue = !value.completed && dayjs(`${value.due.date} ${value.due.time ?? "23:59"}`).isBefore(dayjs());
  const backgroundColor = isOverdue ? OVERDUE_COLOR : undefined;

  const [editOpen, setEditOpen] = useState(false);

  return (
    <EntityCard role={role} onClick={onClick} onContextMenu={() => {}} backgroundColor={backgroundColor}>
      <Grid size={3}>
        <Stack direction="column" spacing={1}>
          <Stack
            direction="row"
            spacing={1}
            sx={{
              alignItems: "center",
            }}
          >
            <CalendarMonthIcon />
            <Typography>{formatDate(value.due.date, { outputFormat: "DD.MM.YYYY" })} </Typography>
          </Stack>
          {value.due.time && (
            <Stack
              direction="row"
              spacing={1}
              sx={{
                alignItems: "center",
              }}
            >
              <TimeIcon />
              {value.due.time}
            </Stack>
          )}
          <Stack
            direction="row"
            spacing={1}
            sx={{
              alignItems: "center",
            }}
          >
            <PersonIcon />
            {value.assignedUser.name}
          </Stack>
        </Stack>
      </Grid>
      <Grid size={6}>{value.description}</Grid>
      <Grid size={2}>
        <Stack
          direction="column"
          spacing={1}
          sx={{
            alignItems: "flex-start",
            justifyContent: "space-between",
          }}
        >
          {value.customer && (
            <Typography
              sx={{
                color: "text.secondary",
              }}
            >
              Klient:{" "}
              <CustomerLink customerId={value.customer.id} target="_blank">
                {value.customer.name}
              </CustomerLink>
            </Typography>
          )}
          {value.parent.type !== "CUSTOMER" && value.customer && (
            <Typography
              sx={{
                color: "text.secondary",
              }}
            >
              <ParentLink parent={value.parent} />
            </Typography>
          )}
          {value.createdBy && (
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
              }}
            >
              Utworzono przez: {value.createdBy.name}
            </Typography>
          )}
        </Stack>
      </Grid>
      <Grid size={1}>
        <TaskActions
          isCompleted={!!value.completed}
          iconsDirection="column"
          canDelete={canDelete}
          onComplete={onComplete}
          onDelete={onDelete}
          onEdit={() => setEditOpen(true)}
        />
        <EditTaskDialog
          open={editOpen}
          onClose={() => setEditOpen(false)}
          onSave={onUpdate}
          initialValues={{
            description: value.description,
            due: value.due,
            assignedUserId: value.assignedUser.id,
          }}
          title="Edytuj zadanie"
          submitButtonText="Zapisz"
        />
      </Grid>
    </EntityCard>
  );
};
